import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { apiConfig } from '../config/auth0-config';

// Types for API responses
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface User {
  id: string;
  email: string;
  auth0Id: string | null;
  firstName: string | null;
  lastName: string | null;
  role: 'ADMIN' | 'MANAGER' | 'COACH' | 'CLIENT';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  permissions: string[];
  auth0Metadata: any;
  emailVerified: boolean;
  lastLogin: string | null;
  loginCount: number;
  auth0LastSync: string | null;
  profilePicture: string | null;
  phoneNumber: string | null;
  timezone: string;
  language: string;
  createdAt: string;
  updatedAt: string;
}

export interface Client {
  id: string;
  userId: string;
  tier: 'BASIC' | 'STANDARD' | 'PREMIUM' | 'ELITE';
  subscriptionType: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY' | null;
  paymentStatus: 'NONE' | 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'REFUNDED' | 'CANCELLED';
  agreedPrice: number | null;
  onboardingProgress: number;
  services: {
    training: boolean;
    nutrition: boolean;
    habitCoaching: boolean;
  } | null;
  assignedCoachId: string | null;
  goals: string[];
  healthConditions: string[];
  preferences: any;
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  } | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  // Relationships
  user?: User;
  assignedCoach?: Coach;
}

export interface Coach {
  id: string;
  userId: string;
  specializations: string[];
  certifications: string[];
  experience: number | null;
  bio: string | null;
  hourlyRate: number | null;
  maxClients: number;
  isActive: boolean;
  performanceMetrics?: any;
  availability?: any;
  createdAt: string;
  updatedAt: string;
  // Relationships
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    profilePicture?: string;
    createdAt: string;
    timezone?: string;
    language?: string;
  };
  assignedClients: Array<{
    id: string;
    tier: string;
    onboardingProgress: number;
    paymentStatus: string;
    user: {
      firstName?: string;
      lastName?: string;
      email: string;
    };
  }>;
  _count?: {
    assignedClients: number;
  };
}

export interface CoachPerformance {
  clientCount: number;
  clientRetentionRate: number;
  averageClientRating: number;
  programsCreated: number;
  onboardingCompletionRate: number;
  revenueGenerated: number;
  avgClientOnboardingTime: number;
  clientSatisfactionScore: number;
}

export interface CoachStats {
  total: number;
  active: number;
  inactive: number;
  averageExperience: number;
  averageClientLoad: number;
  totalClientsServed: number;
  averageRetentionRate: number;
  bySpecialization: Record<string, number>;
  capacityUtilization: number;
}

export interface CreateCoachData {
  firstName: string;
  lastName: string;
  email: string;
  specializations?: string[];
  certifications?: string[];
  experience?: number;
  bio?: string;
  hourlyRate?: number;
  maxClients?: number;
}

export interface UpdateCoachData {
  firstName?: string;
  lastName?: string;
  specializations?: string[];
  certifications?: string[];
  experience?: number;
  bio?: string;
  hourlyRate?: number;
  maxClients?: number;
  isActive?: boolean;
  performanceMetrics?: any;
  availability?: any;
}

export interface CoachQueryOptions {
  page?: number;
  limit?: number;
  isActive?: boolean;
  specialization?: string;
  availableForClients?: boolean;
  search?: string;
}

export interface Exercise {
  id: string;
  name: string;
  description?: string;
  type: 'compound' | 'isolation' | 'cardio';
  repRange: [number, number];
  sets?: number;
  restTime?: number;
  difficulty: number;
  rating: number;
  danger: number;
  symmetryMode: 'symmetrical' | 'unilateral' | 'bilateral';
  muscleActivations: { [muscleGroupId: string]: number };
  equipment: { [equipmentId: string]: number };
  targetAdaptations: { [adaptationId: string]: number };
  instructions: string[];
  techniqueChecklist: string[];
  videoUrl?: string;
  imageUrl?: string;
  tags?: string[];
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface MuscleGroup {
  id: string;
  name: string;
  split: boolean;
  category?: string;
  subCategory?: string;
  description?: string;
  anatomicalRegion?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Equipment {
  id: string;
  name: string;
  category: 'Cardio' | 'Strength' | 'Functional' | 'Accessories' | 'Technology';
  brand: string;
  model: string;
  acquisitionDate: string;
  acquisitionCost: number;
  currentValue: number;
  status: 'Available' | 'In Use' | 'Maintenance' | 'Out of Order' | 'Retired';
  condition: 'Excellent' | 'Good' | 'Fair' | 'Poor';
  location: string;
  maxUsers: number;
  currentUsers: number;
  maintenanceSchedule: 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Annually';
  lastMaintenance: string;
  nextMaintenance: string;
  warrantyExpiry?: string;
  notes: string;
  utilization: number;
  specifications: {
    weight?: number;
    dimensions?: string;
    powerRequirement?: string;
    capacity?: string;
  };
  attachments: string[];
  qrCode?: string;
  serialNumber: string;
}

export interface Adaptation {
  id: string;
  name: string;
  description: string;
  category: string;
  scientificParameters?: string[];
  exerciseAssociations?: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

class ApiService {
  private api: AxiosInstance;
  private getAccessToken?: () => Promise<string>;

  constructor() {
    this.api = axios.create({
      baseURL: apiConfig.baseURL,
      timeout: apiConfig.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  // Set the Auth0 token getter function
  setTokenGetter(getToken: () => Promise<string>) {
    this.getAccessToken = getToken;
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      async (config) => {
        if (this.getAccessToken) {
          try {
            const token = await this.getAccessToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          } catch (error) {
            console.warn('Failed to get access token:', error);
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          console.warn('API returned 401 Unauthorized, attempting token refresh...');
          
          // Prevent infinite retry loops by checking if this is already a retry
          const isRetry = error.config && (error.config as any)._isRetry;
          
          if (isRetry) {
            console.warn('Request already retried once, redirecting to login');
            if (!window.location.pathname.includes('/login')) {
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }
          
          // Try to refresh the token before redirecting
          if (this.getAccessToken) {
            try {
              const newToken = await this.getAccessToken();
              if (newToken && error.config) {
                // Mark this as a retry to prevent infinite loops
                (error.config as any)._isRetry = true;
                
                // Retry the request with the new token
                error.config.headers.Authorization = `Bearer ${newToken}`;
                return this.api.request(error.config);
              }
            } catch (tokenError: any) {
              console.error('Token refresh failed:', tokenError);
              
              // Check if it's a refresh token expiration
              if (tokenError.message === 'REFRESH_TOKEN_EXPIRED') {
                console.log('Refresh token expired, redirecting to login...');
                // Only redirect to login if we're not already on the login page
                if (!window.location.pathname.includes('/login')) {
                  window.location.href = '/login';
                }
                return Promise.reject(error);
              }
            }
          }
          
          // For other 401 errors, don't immediately redirect - let the component handle it
          console.warn('Authentication failed, but not redirecting automatically');
        }
        return Promise.reject(error);
      }
    );
  }

  // Generic request method
  private async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    url: string,
    data?: any
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.request({
        method,
        url,
        data,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return {
          success: false,
          error: error.response?.data?.message || error.message,
        };
      }
      return {
        success: false,
        error: 'An unexpected error occurred',
      };
    }
  }

  // Health endpoints
  async healthCheck() {
    return this.request('GET', '/api/health');
  }

  async detailedHealth() {
    return this.request('GET', '/api/health/detailed');
  }

  // Auth endpoints
  async getAuthStatus() {
    return this.request('GET', '/api/auth/status');
  }

  async getProfile() {
    return this.request<User>('GET', '/api/auth/profile');
  }

  // User management
  async getUsers(params?: { 
    page?: number; 
    limit?: number; 
    role?: string; 
    search?: string 
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<{ users: User[]; total: number; page: number; limit: number }>
      ('GET', `/api/users?${query}`);
  }

  async getUserById(id: string) {
    return this.request<User>('GET', `/api/users/${id}`);
  }

  async createUser(userData: Partial<User>) {
    return this.request<User>('POST', '/api/users', userData);
  }

  async updateUser(id: string, userData: Partial<User>) {
    return this.request<User>('PUT', `/api/users/${id}`, userData);
  }

  async deleteUser(id: string) {
    return this.request('DELETE', `/api/users/${id}`);
  }

  // Client management
  async getClients(params?: { 
    page?: number; 
    limit?: number; 
    tier?: string; 
    coachId?: string 
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<{ clients: Client[]; total: number; page: number; limit: number }>
      ('GET', `/api/clients?${query}`);
  }

  async getClientById(id: string) {
    return this.request<Client>('GET', `/api/clients/${id}`);
  }

  async createClient(clientData: Partial<Client>) {
    return this.request<Client>('POST', '/api/clients', clientData);
  }

  async updateClient(id: string, clientData: Partial<Client>) {
    return this.request<Client>('PUT', `/api/clients/${id}`, clientData);
  }

  async assignCoach(clientId: string, coachId: string) {
    return this.request('PATCH', `/api/clients/${clientId}/coach`, { coachId });
  }

  // Coach management
  async getCoaches(params?: CoachQueryOptions) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<{ data: Coach[]; pagination: any }>
      ('GET', `/api/coaches?${query}`);
  }

  async getAvailableCoaches(params?: { specialization?: string }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<{ data: Coach[]; pagination: any }>
      ('GET', `/api/coaches/available?${query}`);
  }

  async getCoachById(id: string) {
    return this.request<{ data: Coach }>('GET', `/api/coaches/${id}`);
  }

  async createCoach(data: CreateCoachData & { phoneNumber?: string }) {
    console.log('=== API createCoach DEBUG ===');
    console.log('1. Create data received:', data);
    
    try {
      // Separate coach data from user data
      const { phoneNumber, ...coachData } = data;
      
      console.log('2. After destructuring - phoneNumber:', phoneNumber);
      console.log('3. After destructuring - coachData:', coachData);
      
      // For CREATE: Ensure arrays are properly formatted (backend expects arrays)
      if (coachData.specializations) {
        console.log('4a. CREATE specializations (should be array):', coachData.specializations, 'isArray:', Array.isArray(coachData.specializations));
        coachData.specializations = Array.isArray(coachData.specializations) 
          ? coachData.specializations.filter(s => s && typeof s === 'string')
          : [];
      }
      if (coachData.certifications) {
        console.log('5a. CREATE certifications (should be array):', coachData.certifications, 'isArray:', Array.isArray(coachData.certifications));
        coachData.certifications = Array.isArray(coachData.certifications)
          ? coachData.certifications.filter(c => c && typeof c === 'string')
          : [];
      }
      
      console.log('6. Final CREATE data with arrays:', coachData);
      
      // Create the coach (which also creates the user)
      const response = await this.request<{ data: Coach; message: string }>('POST', '/api/coaches', coachData);
      
      // If coach creation was successful and we have a phone number, update the user
      if (response.success && response.data && phoneNumber) {
        try {
          const coach = response.data.data;
          if (coach.userId) {
            // Update the user with phone number (this might need to be adjusted based on your user update API)
            await this.request('PATCH', `/api/users/${coach.userId}`, { phoneNumber });
          }
        } catch (phoneError) {
          console.warn('Coach created but phone number update failed:', phoneError);
        }
      }
      
      return response;
    } catch (error) {
      console.error('Error in createCoach:', error);
      throw error;
    }
  }

  async updateCoach(id: string, data: UpdateCoachData & { phoneNumber?: string }) {
    console.log('=== API updateCoach DETAILED DEBUG ===');
    console.log('1. Raw data received by API:', data);
    console.log('1a. Data type check:', typeof data);
    console.log('1b. Data is array?', Array.isArray(data));
    console.log('1c. Data keys:', Object.keys(data));
    
    try {
      // Separate coach data from user data
      const { phoneNumber, ...coachData } = data;
      
      console.log('2. After destructuring - phoneNumber:', phoneNumber, 'type:', typeof phoneNumber);
      console.log('3. After destructuring - coachData:', coachData);
      
      // Check each field type
      Object.keys(coachData).forEach(key => {
        const value = (coachData as any)[key];
        console.log(`Field ${key}:`, value, 'type:', typeof value, 'isArray:', Array.isArray(value));
        if (Array.isArray(value)) {
          console.log(`  ${key} array contents:`, value.map((item, idx) => `[${idx}]: ${item} (${typeof item})`));
        }
      });
      
      // Ensure arrays are properly formatted and clean  
      if (coachData.specializations) {
        console.log('4a. Processing specializations:', coachData.specializations);
        // Backend expects arrays - ensure it's a proper array
        coachData.specializations = Array.isArray(coachData.specializations) 
          ? coachData.specializations.filter(s => s && typeof s === 'string')
          : [];
        console.log('4b. After processing specializations as array:', coachData.specializations);
      }
      if (coachData.certifications) {
        console.log('5a. Processing certifications:', coachData.certifications);
        // Backend expects arrays - ensure it's a proper array
        coachData.certifications = Array.isArray(coachData.certifications)
          ? coachData.certifications.filter(c => c && typeof c === 'string')
          : [];
        console.log('5b. After processing certifications as array:', coachData.certifications);
      }
      
      // Ensure numeric fields are properly typed
      if (coachData.hourlyRate !== undefined) {
        console.log('6a. Processing hourlyRate:', coachData.hourlyRate, 'type:', typeof coachData.hourlyRate);
        coachData.hourlyRate = coachData.hourlyRate === null ? undefined : Number(coachData.hourlyRate);
        console.log('6b. After processing hourlyRate:', coachData.hourlyRate, 'type:', typeof coachData.hourlyRate);
      }
      
      // Ensure string fields are strings
      if (coachData.firstName !== undefined) {
        console.log('7a. Processing firstName:', coachData.firstName, 'type:', typeof coachData.firstName);
        coachData.firstName = String(coachData.firstName);
        console.log('7b. After processing firstName:', coachData.firstName, 'type:', typeof coachData.firstName);
      }
      if (coachData.lastName !== undefined) {
        console.log('8a. Processing lastName:', coachData.lastName, 'type:', typeof coachData.lastName);
        coachData.lastName = String(coachData.lastName);
        console.log('8b. After processing lastName:', coachData.lastName, 'type:', typeof coachData.lastName);
      }
      if (coachData.bio !== undefined) {
        console.log('9a. Processing bio:', coachData.bio, 'type:', typeof coachData.bio);
        coachData.bio = String(coachData.bio);
        console.log('9b. After processing bio:', coachData.bio, 'type:', typeof coachData.bio);
      }
      
      console.log('10. Final cleaned coach data before field validation:', coachData);
      
      // Verify no unexpected fields exist and check data structure
      const allowedFields = ['firstName', 'lastName', 'specializations', 'certifications', 'experience', 'bio', 'hourlyRate', 'maxClients', 'isActive', 'performanceMetrics', 'availability'];
      const coachDataFields = Object.keys(coachData);
      const unexpectedFields = coachDataFields.filter(field => !allowedFields.includes(field));
      
      if (unexpectedFields.length > 0) {
        console.error('UNEXPECTED FIELDS DETECTED:', unexpectedFields);
        // Remove unexpected fields
        unexpectedFields.forEach(field => delete (coachData as any)[field]);
        console.log('11. After removing unexpected fields:', coachData);
      }
      
      // Final validation - ensure no nested objects or functions
      const finalValidation = Object.entries(coachData).map(([key, value]) => {
        const type = typeof value;
        const isArray = Array.isArray(value);
        const isObject = type === 'object' && !isArray && value !== null;
        const isFunction = type === 'function';
        
        return {
          key,
          value,
          type,
          isArray,
          isObject,
          isFunction,
          valid: !isObject && !isFunction
        };
      });
      
      console.log('12. Final validation check:', finalValidation);
      const invalidFields = finalValidation.filter(f => !f.valid);
      if (invalidFields.length > 0) {
        console.error('INVALID FIELD TYPES DETECTED:', invalidFields);
      }
      
      // Create a completely clean object with only primitive values and arrays
      const cleanData: any = {};
      if (coachData.firstName !== undefined) cleanData.firstName = String(coachData.firstName);
      if (coachData.lastName !== undefined) cleanData.lastName = String(coachData.lastName);
      if (coachData.bio !== undefined) cleanData.bio = String(coachData.bio);
      if (coachData.hourlyRate !== undefined) cleanData.hourlyRate = Number(coachData.hourlyRate);
      
      // Keep these as arrays since backend expects arrays
      if (coachData.specializations !== undefined) cleanData.specializations = [...(coachData.specializations || [])];
      if (coachData.certifications !== undefined) cleanData.certifications = [...(coachData.certifications || [])];
      
      console.log('13. Completely clean rebuilt data with arrays:', cleanData);
      console.log('14. About to call backend PUT with clean data. JSON stringified:', JSON.stringify(cleanData));
      
      // Update the coach
      const response = await this.request<{ data: Coach; message: string }>('PUT', `/api/coaches/${id}`, cleanData);
      console.log('15. Backend response:', response);
      
      // If coach update was successful and we have a phone number, update the user
      if (response.success && response.data && phoneNumber !== undefined) {
        try {
          const coach = response.data.data;
          if (coach.userId) {
            console.log('16. Updating user phone number separately:', phoneNumber);
            await this.request('PATCH', `/api/users/${coach.userId}`, { phoneNumber });
          }
        } catch (phoneError) {
          console.warn('Coach updated but phone number update failed:', phoneError);
        }
      }
      
      return response;
    } catch (error) {
      console.error('Error in updateCoach:', error);
      throw error;
    }
  }

  async activateCoach(id: string) {
    return this.request<{ data: Coach; message: string }>('PATCH', `/api/coaches/${id}/activate`);
  }

  async deactivateCoach(id: string) {
    return this.request<{ data: Coach; message: string }>('PATCH', `/api/coaches/${id}/deactivate`, undefined);
  }

  async getCoachClients(coachId: string, params?: { page?: number; limit?: number }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<{ data: Client[]; pagination: any }>
      ('GET', `/api/coaches/${coachId}/clients?${query}`);
  }

  async getCoachPerformance(coachId: string) {
    return this.request<{ data: CoachPerformance }>('GET', `/api/coaches/${coachId}/performance`);
  }

  async updateCoachPerformance(coachId: string) {
    return this.request<{ data: Coach; message: string }>
      ('PATCH', `/api/coaches/${coachId}/performance/update`);
  }

  async searchCoaches(query: string, options?: { 
    specialization?: string; 
    availableForClients?: boolean 
  }) {
    const params = new URLSearchParams({
      q: query,
      ...options
    } as any).toString();
    return this.request<{ data: Coach[]; pagination: any }>
      ('GET', `/api/coaches/search?${params}`);
  }

  // Exercise management
  async getExercises(params?: { 
    page?: number; 
    limit?: number; 
    type?: string; 
    difficulty?: string;
    search?: string;
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<{ exercises: Exercise[]; total: number; page: number; limit: number }>
      ('GET', `/api/exercises?${query}`);
  }

  async getExerciseById(id: string) {
    return this.request<Exercise>('GET', `/api/exercises/${id}`);
  }

  async createExercise(exerciseData: Partial<Exercise>) {
    return this.request<Exercise>('POST', '/api/exercises', exerciseData);
  }

  async updateExercise(id: string, exerciseData: Partial<Exercise>) {
    return this.request<Exercise>('PUT', `/api/exercises/${id}`, exerciseData);
  }

  async deleteExercise(id: string) {
    return this.request('DELETE', `/api/exercises/${id}`);
  }

  async searchExercises(query: string, filters?: { type?: string; muscleGroup?: string; equipment?: string }) {
    const params = new URLSearchParams({ q: query });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
    }
    return this.request<{exercises: Exercise[]; suggestions: string[]; filters: any}>('GET', `/api/exercises/search?${params.toString()}`);
  }

  // Muscle Groups management
  async getMuscleGroups(params?: { 
    category?: string; 
    region?: string 
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<MuscleGroup[]>('GET', `/api/muscle-groups?${query}`);
  }

  async getMuscleGroupById(id: string) {
    return this.request<MuscleGroup>('GET', `/api/muscle-groups/${id}`);
  }

  async createMuscleGroup(muscleGroupData: Partial<MuscleGroup>) {
    return this.request<MuscleGroup>('POST', '/api/muscle-groups', muscleGroupData);
  }

  async updateMuscleGroup(id: string, muscleGroupData: Partial<MuscleGroup>) {
    return this.request<MuscleGroup>('PUT', `/api/muscle-groups/${id}`, muscleGroupData);
  }

  async deleteMuscleGroup(id: string) {
    return this.request('DELETE', `/api/muscle-groups/${id}`);
  }

  async getMuscleGroupStats() {
    return this.request('GET', '/api/muscle-groups/stats');
  }

  // Equipment management
  async getEquipment(params?: { 
    page?: number; 
    limit?: number; 
    type?: string; 
    status?: string;
    condition?: string;
    location?: string;
    search?: string;
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<{ data: Equipment[]; pagination?: any }>('GET', `/api/equipment?${query}`);
  }

  async getEquipmentById(id: string) {
    return this.request<Equipment>('GET', `/api/equipment/${id}`);
  }

  async createEquipment(equipmentData: Partial<Equipment>) {
    return this.request<Equipment>('POST', '/api/equipment', equipmentData);
  }

  async updateEquipment(id: string, equipmentData: Partial<Equipment>) {
    return this.request<Equipment>('PUT', `/api/equipment/${id}`, equipmentData);
  }

  async deleteEquipment(id: string) {
    return this.request('DELETE', `/api/equipment/${id}`);
  }

  async searchEquipment(query: string, filters?: { type?: string; status?: string }) {
    const params = new URLSearchParams({ q: query });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
    }
    return this.request<{equipment: Equipment[]; suggestions: string[]}>('GET', `/api/equipment/search?${params.toString()}`);
  }

  async getEquipmentStats() {
    return this.request('GET', '/api/equipment/stats');
  }

  // Adaptations management (placeholder - to be implemented)
  async getAdaptations(params?: { category?: string }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request<Adaptation[]>('GET', `/api/adaptations?${query}`);
  }

  async getAdaptationById(id: string) {
    return this.request<Adaptation>('GET', `/api/adaptations/${id}`);
  }

  async createAdaptation(adaptationData: Partial<Adaptation>) {
    return this.request<Adaptation>('POST', '/api/adaptations', adaptationData);
  }

  async updateAdaptation(id: string, adaptationData: Partial<Adaptation>) {
    return this.request<Adaptation>('PUT', `/api/adaptations/${id}`, adaptationData);
  }

  async deleteAdaptation(id: string) {
    return this.request('DELETE', `/api/adaptations/${id}`);
  }

  async getAdaptationStats() {
    return this.request('GET', '/api/adaptations/stats');
  }

  // Exercise adaptations
  async getExerciseAdaptations(exerciseId: string, clientData?: any) {
    return this.request('POST', `/api/exercises/${exerciseId}/adaptations`, { clientData });
  }

  // Library statistics
  async getLibraryStats() {
    return this.request('GET', '/api/libraries/stats');
  }

  // Statistics
  async getUserStats() {
    return this.request('GET', '/api/users/stats');
  }

  async getClientStats() {
    return this.request('GET', '/api/clients/stats');
  }

  async getCoachStats() {
    return this.request<{ data: CoachStats }>('GET', '/api/coaches/stats');
  }

  async getExerciseStats() {
    return this.request('GET', '/api/exercises/stats');
  }
}

// Create and export singleton instance
export const apiService = new ApiService();
export default apiService; 